/* RESPONSIBLE TEAM: team-phone */

import EditorConfig, {
  type PathConfigGenerationParams,
  type AttributeGroupList,
} from 'embercom/objects/visual-builder/configuration/editor';
import { PhoneCallEndedPathConfig } from 'embercom/objects/visual-builder/configuration/path';
import type MutableArray from '@ember/array/mutable';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';

const CALL_STATE_ATTRIBUTE_IDS = [
  'conversation.custom_attribute.last_inbound_call_state',
  'conversation.custom_attribute.last_outbound_call_state',
];

export default class PhoneCallEndedEditorConfig extends EditorConfig {
  public targetChannels: MutableArray<string>;

  constructor({
    targetChannels,
    areStepPaywallsActive,
    workflow,
  }: {
    targetChannels: MutableArray<string>;
    areStepPaywallsActive: boolean;
    workflow?: Workflow;
  }) {
    super({ areStepPaywallsActive, workflow });
    this.targetChannels = targetChannels;
  }

  generatePathConfig({ path, editorState }: PathConfigGenerationParams) {
    return new PhoneCallEndedPathConfig({
      path,
      editorState,
      supportsLocalAttributes: this.supportsLocalAttributes,
    });
  }

  get supportsConversationChannelTargeting(): boolean {
    return false;
  }

  // Restricts list option pickers (e.g. last_inbound_call_state) to options
  // tagged server-side with `available_in: ["phone-call-ended"]`. See
  // PHONE_CALL_ENDED_TRIGGER_CONTEXT in
  // conversation_attributes/system_defined_attributes.rb.
  get triggerContext(): string {
    return 'phone-call-ended';
  }

  // Expose `last_inbound_call_state` and `last_outbound_call_state` as targeting
  // attributes specifically for this trigger. The value picker further restricts
  // which list options appear by reading the workflow trigger's `available_in`
  // tags (see `optional-select.js#conversationAttributeListOptions`), so the
  // editor only needs to surface the attributes here — no option-level mutation.
  private get callStateAttributeGroup(): { heading: string; attributes: any[] } | null {
    let allConvAttrs = this.attributeService.conversationCustomAttributes;
    if (!allConvAttrs) {
      return null;
    }

    let callStateAttributes = allConvAttrs.filter((attr: any) => {
      let id = attr.identifier || attr.id || attr.get?.('identifier');
      return CALL_STATE_ATTRIBUTE_IDS.includes(id);
    });

    if (callStateAttributes.length === 0) {
      return null;
    }

    return {
      heading: this.intl.t(
        'operator.custom-bot.trigger.phone-call-ended.call-state-attribute-heading',
      ),
      attributes: callStateAttributes,
    };
  }

  get runtimeMatchingTargetingAttributes(): AttributeGroupList {
    let group = this.callStateAttributeGroup;
    if (!group) {
      return super.runtimeMatchingTargetingAttributes;
    }
    return [...super.runtimeMatchingTargetingAttributes, group];
  }

  get allRuntimeMatchingTargetingAttributes(): AttributeGroupList {
    let group = this.callStateAttributeGroup;
    if (!group) {
      return super.allRuntimeMatchingTargetingAttributes;
    }
    return [...super.allRuntimeMatchingTargetingAttributes, group];
  }

  // The trigger audience editor (side panel) reads
  // `workflowMatchingTargetingAttributes`. Customers select call-state
  // predicates against the trigger itself, not just at runtime steps —
  // e.g. "fire this workflow only when the inbound call was abandoned."
  //
  // The base `EditorConfig.workflowMatchingTargetingAttributes` returns
  // `null`, so we source the normal workflow audience attributes the same
  // way `TicketTriggerEditorConfig` does — via
  // `attributeService.attributesToGroupList(visualBotBuilderConditionalAttributes)`
  // — and then append the call-state group. This lets users combine
  // phone-call-ended with person/company/conversation predicates.
  get workflowMatchingTargetingAttributes(): AttributeGroupList | null {
    let base: AttributeGroupList = this.attributeService.attributesToGroupList(
      this.attributeService.visualBotBuilderConditionalAttributes,
    );
    let group = this.callStateAttributeGroup;
    if (!group) {
      return base.length ? base : null;
    }
    return [...base, group];
  }
}
